import { ColumnType } from 'plume-ui/dist/components/TableV2/types';

import i18n from '../../../../utils/i18n';
import { GetCriteriaNameFn } from '../../../Monitor/hooks/useCriteriaNames';
import { IssueMatrixStat } from '../../types';

export type IssueMatrixTableRow = {
  issueCategory: string;
  issueType: string;
  locationCount: string;
  percentLocations: string;
  nonOnboardedLocationCount: string;
};

export const parseIssueMatrixDataForTable = (
  data: IssueMatrixStat[],
  locationCount: number,
  category: string,
  getCriteriaName: GetCriteriaNameFn,
): IssueMatrixTableRow[] =>
  data
    .filter((stat) => category === stat.issueCategory || category === '')
    .map((stat) => ({
      issueCategory: getCriteriaName(stat.issueCategory),
      issueType: getCriteriaName(stat.issueType),
      locationCount: (stat.locationCount || 0).toString(),
      percentLocations: parsePercentage(stat.locationCount, locationCount, 2),
      nonOnboardedLocationCount: (
        stat.nonOnboardedLocationCount || 0
      ).toString(),
    }));

export const getDefaultCategory = () => ({
  label: i18n.t('all'),
  value: '',
});

export const prepareDropdownItems = (
  data: IssueMatrixStat[],
  getCriteriaName: GetCriteriaNameFn,
) => {
  const res = [getDefaultCategory()];
  data
    .filter(
      (value, index, array) =>
        array.indexOf(
          array.find((item) => item.issueCategory === value.issueCategory)!,
        ) === index,
    )
    .forEach((stat) => {
      res.push({
        label: getCriteriaName(stat.issueCategory),
        value: stat.issueCategory,
      });
    });
  return res;
};

export const getIssueMatrixTableHeaders = (): ColumnType<IssueMatrixTableRow>[] => [
  {
    title: i18n.t('healthSummary.issueMatrixTable.headers.issueCategory'),
    key: 'issueCategory',
    dataIndex: 'issueCategory',
    render: (row) => (
      <span style={{ width: 'fit-content' }}>{row.issueCategory}</span>
    ),
  },
  {
    title: i18n.t('healthSummary.issueMatrixTable.headers.issueType'),
    key: 'issueType',
    dataIndex: 'issueType',
    render: (row) => (
      <span style={{ width: 'fit-content' }}>{row.issueType}</span>
    ),
  },
  {
    title: i18n.t('healthSummary.issueMatrixTable.headers.noAffectedLocations'),
    key: 'locationCount',
    dataIndex: 'locationCount',
    render: (row) => (
      <span style={{ textAlign: 'end', width: '100%' }}>
        {row.locationCount}
      </span>
    ),
    sorter: (a, b) => {
      return parseFloat(b.locationCount) - parseFloat(a.locationCount);
    },
  },
  {
    title: i18n.t('healthSummary.issueMatrixTable.headers.pctAffectedLocations'),
    key: 'percentLocations',
    dataIndex: 'percentLocations',
    render: (row) => (
      <span style={{ textAlign: 'end', width: '100%' }}>
        {row.percentLocations}
      </span>
    ),
    sorter: (a, b) => {
      return parseFloat(b.percentLocations) - parseFloat(a.percentLocations);
    },
  },
  {
    title: i18n.t(
      'healthSummary.issueMatrixTable.headers.noLocationsIncompleteOnboarding',
    ),
    key: 'nonOnboardedLocationCount',
    dataIndex: 'nonOnboardedLocationCount',
    render: (row) => (
      <span style={{ textAlign: 'end', width: '100%' }}>
        {row.nonOnboardedLocationCount}
      </span>
    ),
    sorter: (a, b) => {
      return parseFloat(b.nonOnboardedLocationCount) - parseFloat(a.nonOnboardedLocationCount);
    },
  },
];

export const parsePercentage = (
  statLocationCount: number,
  locationCount: number,
  decimals: number,
) => {
  if (locationCount === 0) return '0.00%';
  const percentage = (100 * statLocationCount) / locationCount;
  const formattedPercentage = percentage.toFixed(decimals);
  return `${formattedPercentage}%`;
};
